import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { Suspense, useEffect, useMemo, useState } from "react";

import { getImageUrl } from "@/utils/getImageUrl";
import { cn } from "@/utils/cn";
import { toGlobalId } from "graphql-relay";
import { graphql, useLazyLoadQuery } from "react-relay";
import type { resizableMediaNodeViewQuery } from "./__generated__/resizableMediaNodeViewQuery.graphql";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { ImageFullscreen } from "@/ui/image-fullscreen";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/icon";
import { Null } from "@/components/null";

export const ResizableMediaNodeComponent = ({
	node,
	editor,
	updateAttributes,
}: NodeViewProps) => {
	const queryData = useLazyLoadQuery<resizableMediaNodeViewQuery>(
		graphql`
		query resizableMediaNodeViewQuery($id: ID!, $skip: Boolean!) {
      node(id: $id) @skip(if: $skip) {
         ...on ImageFile {
           id
           signedUrl
         }
       }
		}
   `,
		{
			id: node.attrs.fileId ? toGlobalId("ImageFile", node.attrs.fileId) : "",
			skip: !node.attrs.fileId?.length,
		},
	);

	const data = queryData.node;
	const [_isAlign, setIsAlign] = useState<boolean>();

	useEffect(() => {
		setIsAlign(node.attrs.dataAlign);
	}, [node.attrs?.dataAlign]);

	const justify: Record<string, string[]> = {
		left: ["justify-start"],
		center: ["justify-center"],
		right: ["justify-end"],
	};

	const imgSrc = useMemo(() => {
		const options: Parameters<typeof getImageUrl>[1] = {
			quality: 90,
			height: 400,
		};

		// eslint-disable-next-line immutable/no-mutation
		return getImageUrl(data?.signedUrl || node.attrs.src, options);
	}, [node.attrs.src, data?.signedUrl]);

	if (!editor?.isEditable) {
		return (
			<NodeViewWrapper
				as="article"
				className={cn(
					"media-node-view not-prose w-full transition-all ease-in-out",
				)}
			>
				<div
					className={cn(
						"flex w-full items-center justify-center",
						justify[node.attrs.dataAlign]?.join(""),
					)}
				>
					<Dialog>
						<Tooltip>
							<TooltipTrigger asChild>
								<DialogTrigger asChild>
									<img
										src={imgSrc}
										draggable={false}
										className={
											"h-full w-full rounded-md max-w-full max-h-[300px] object-contain"
										}
										alt={node.attrs.alt || "user added media"}
									/>
								</DialogTrigger>
							</TooltipTrigger>

							<TooltipContent>
								Clique para carregar imagem em tela cheia
							</TooltipContent>
						</Tooltip>

						<ImageFullscreen src={data?.signedUrl || node.attrs.src} />
					</Dialog>
				</div>
			</NodeViewWrapper>
		);
	}

	return (
		<div
			className={cn(
				"flex w-full items-center justify-center",
				justify[node.attrs.dataAlign]?.join(""),
			)}
		>
			<HoverCard>
				<HoverCardTrigger>
					<img
						src={imgSrc}
						draggable={false}
						className={
							"h-full w-full rounded-md max-w-[50%] max-h-[250px] object-contain"
						}
						alt={node.attrs.alt || "user added media"}
					/>
				</HoverCardTrigger>

				<HoverCardContent className="flex flex-row gap-1 w-fit p-1.5">
					<Button
						variant={node.attrs.dataAlign === "left" ? "default" : "ghost"}
						disabled={node.attrs.dataAlign === "left"}
						onClick={() => {
							updateAttributes({ dataAlign: "left" });
						}}
					>
						<Icon name="TextAlignStart" />
					</Button>
					<Button
						variant={node.attrs.dataAlign === "center" ? "default" : "ghost"}
						disabled={node.attrs.dataAlign === "center"}
						onClick={() => {
							updateAttributes({ dataAlign: "center" });
						}}
					>
						<Icon name="TextAlignCenter" />
					</Button>
					<Button
						variant={node.attrs.dataAlign === "right" ? "default" : "ghost"}
						disabled={node.attrs.dataAlign === "right"}
						onClick={() => {
							updateAttributes({ dataAlign: "right" });
						}}
					>
						<Icon name="TextAlignEnd" />
					</Button>
				</HoverCardContent>
			</HoverCard>
		</div>
	);
};

export const ResizableMediaNodeView = (props: NodeViewProps) => {
	return (
		<NodeViewWrapper
			as="article"
			data-drag-handle=""
			className={cn(
				"media-node-view not-prose w-full transition-all ease-in-out",
			)}
		>
			<Suspense fallback={<Null />}>
				<ResizableMediaNodeComponent {...props} />
			</Suspense>
		</NodeViewWrapper>
	);
};
